import { EffectiveSessionTimeoutSource, MFAScope } from '@heidi/schema/ml-scribe';
import { type EffectiveSessionTimeout } from '@heidi/workspace/contracts';
import { useCurrentUserQueryResult } from '@heidi/workspace/data';
import { useAtomValue } from 'jotai';
import { useMemo } from 'react';

import { complianceNotificationDismissAtom } from './dismiss-atoms';

/** Session timeout is active when enforced by user choice or cohort default. */
function isSessionInactivityTimeoutEnabled(
  source: EffectiveSessionTimeoutSource | undefined
): boolean {
  return (
    source === EffectiveSessionTimeoutSource.USER ||
    source === EffectiveSessionTimeoutSource.COHORT_DEFAULT
  );
}

/** Session timeout is externally enforced — user cannot opt out from the card. */
function isSessionInactivityTimeoutEnforced(
  source: EffectiveSessionTimeoutSource | undefined
): boolean {
  return source === EffectiveSessionTimeoutSource.ORGANIZATION;
}

/**
 * `isFlagEnabled` is a PARAMETER rather than a hook call. The rollout keys
 * resolve through PostHog (`useFeatureFlagEnabled`), and this package's
 * `./flags` door has no PostHog surface — converting the read onto
 * `useBooleanFlag` would change which provider answers. The app reads the
 * getters and crosses the booleans here, the same way
 * `effectiveSessionTimeout` already does for the inactivity half.
 */
export function useMfaSetupNotificationEligibility(isFlagEnabled: boolean): boolean {
  const dismissState = useAtomValue(complianceNotificationDismissAtom);
  const { data: user } = useCurrentUserQueryResult((data) => ({
    isMfaEnabled: data.user_mfa_info?.is_mfa_enabled ?? false,
    isMfaEditable: data.user_mfa_info?.is_mfa_editable_by_user ?? false,
    scope: data.user_mfa_info?.scope,
    isSsoLogin: data.is_sso_login ?? false,
    isSocialLogin: data.is_social_login ?? false,
  }));

  return useMemo(() => {
    if (!isFlagEnabled || dismissState.mfaSetup || !user) return false;
    // Feature already enabled — nothing to recommend.
    if (user.isMfaEnabled) return false;
    // Region/org enforced — not an opt-in prompt.
    if (user.scope === MFAScope.REGION) return false;
    if (user.isSocialLogin) return false;
    if (user.isSsoLogin) return false;
    return user.isMfaEditable;
  }, [dismissState.mfaSetup, isFlagEnabled, user]);
}

/**
 * `effectiveSessionTimeout` is a PARAMETER rather than a hook call, and it is
 * the one thing that did not come behind the door with this hook.
 *
 * `useEffectiveSessionTimeout` is not a flag: it merges `GET /user` with a
 * user-scoped localStorage override, WRITES that override back, and decides what
 * `AutoLogout` enforces. It is the settings tree's, and the settings tree has no
 * package — so what crosses is its resolved value, read by whoever composes the
 * surface. `null` keeps the meaning it always had: the user is not in the
 * session-timeout rollout cohort, so there is nothing to recommend.
 *
 * A VALUE and not a `() => …` thunk on purpose. The freshness law that makes a
 * flag cross as a thunk is about reads that happen outside render — a `queryFn`,
 * a retry — where a boolean captured at render is stale by the time it is read.
 * This one is read during render, from a hook whose own subscription re-renders
 * the caller when the timeout changes, so the value is as fresh as a call here
 * would be.
 *
 * `isFlagEnabled` is the same shape for the same reason: PostHog, app-resolved.
 */
export function useInactivityLogoutNotificationEligibility(
  effectiveSessionTimeout: EffectiveSessionTimeout | null,
  isFlagEnabled: boolean
): boolean {
  const dismissState = useAtomValue(complianceNotificationDismissAtom);

  return useMemo(() => {
    if (!isFlagEnabled || dismissState.inactivityLogout) return false;
    // null = user is not in the session-timeout rollout cohort (Reza, GRO-3989).
    if (effectiveSessionTimeout == null) return false;

    const { source, is_editable_by_user: isEditable } = effectiveSessionTimeout;

    if (isSessionInactivityTimeoutEnforced(source)) return false;
    if (isSessionInactivityTimeoutEnabled(source)) return false;

    return (
      isEditable !== false &&
      (source === EffectiveSessionTimeoutSource.OPT_IN_AVAILABLE ||
        source === EffectiveSessionTimeoutSource.USER_DISABLED)
    );
  }, [dismissState.inactivityLogout, effectiveSessionTimeout, isFlagEnabled]);
}
